import type React from "react";
import {useOutletContext} from "react-router";
import {Box, Stack, Typography} from "@mui/material";
import {CustomCard} from "~/components/Card/Card";
import {Tag} from "~/components/tag/Tag";
import {SupplierCountryFlag} from "~/pages/supplier-360/components/SupplierCountryFlag";
import {
    SUPPLIER_CONTACT_TYPES,
    type SupplierClusterMember,
    type SupplierCountry,
    type SupplierDetailOutletContext,
    type SupplierGoodsAndServicesEntry,
    type SupplierSummaryEntry,
} from "../../types/Supplier360Types";
import {
    useSupplierEnrichmentProfileQuery
} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierEnrichmentProfileQuery";
import {
    useSupplierClusterMembersByEntityQuery
} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierClusterMembersByEntityQuery";
import {useSupplierContactsQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierContactsQuery";
import {useSupplierSuggestionsQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierSuggestionsQuery";
import {SupplierInfoField} from "./components/SupplierInfoField";
import {GoodsAndServicesField} from "./components/GoodsAndServicesField";
import {DomainField} from "./components/DomainField";
import {DomainSuggestion} from "./components/DomainSuggestion";
import {AliasSuggestions} from "./components/AliasSuggestions";
import {ClusterSection} from "./components/ClusterSection";
import {SupplierContactsField} from "./components/SupplierContactsField";
import {ParentField} from "./components/ParentField";
import {SegmentationBlock} from "./components/SegmentationBlock";
import {SpendSummarySection} from "./components/SpendSummarySection";
import {SpendTrendSection} from "./components/SpendTrendSection";
import {CompanyFactsCard} from "./components/CompanyFactsCard";
import {WorkforceFootprintCard} from "./components/WorkforceFootprintCard";
import {CommercialLeverageCard} from "./components/CommercialLeverageCard";


export const SupplierOverviewTab: React.FC = () => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const {data: clusterResp, isFetching: clusterFetching} = useSupplierClusterMembersByEntityQuery(entity.id);
    // The whole cluster, unpaginated — so a count off these rows is the real one.
    const clusterMembers = clusterResp ?? [];

    return (
        <Stack gap={2}>
            <IdentitySection
                entityId={entity.id}
                entityName={entity.canonical_name}
                entityCountry={entity.se_country}
                entityCity={entity.se_city}
                summary={entity.summary}
                goodsAndServices={entity.goods_and_services}
                clusterMembers={clusterMembers}
            />

            <ClusterSection
                rows={clusterMembers}
                count={clusterMembers.length}
                isLoading={clusterFetching}
                entityId={entity.id}
                databagId={entity.databag_id}
            />

            <Stack direction={{xs: 'column', lg: 'row'}} gap={2} alignItems="stretch">
                <SpendTrendSection entityId={entity.id}/>
                <SpendSummarySection entityId={entity.id}/>
            </Stack>

            <Stack direction={{xs: 'column', lg: 'row'}} gap={2} alignItems="stretch">
                <CompanyFactsCard entityId={entity.id}/>
                <WorkforceFootprintCard entityId={entity.id}/>
                <CommercialLeverageCard entityId={entity.id} supplierName={entity.canonical_name}/>
            </Stack>
        </Stack>
    );
};

const IdentitySection: React.FC<{
    entityId: number;
    entityName: string;
    entityCountry: SupplierCountry | null;
    entityCity: string;
    /** Both arrive on the entity itself (§2.1) — neither section needs a fetch of its own. */
    summary: SupplierSummaryEntry[];
    /** Entries here hold a list of strings each, unlike `summary`'s one (§2.1). */
    goodsAndServices: SupplierGoodsAndServicesEntry[];
    clusterMembers: SupplierClusterMember[];
}> = ({entityId, entityName, entityCountry, entityCity, summary, goodsAndServices, clusterMembers}) => {
    const {data: enrichment, isLoading: enrichmentLoading} = useSupplierEnrichmentProfileQuery(entityId);
    // One fetch for all three multi-selects, split by `contact_type` client-side (§3.3).
    const {data: contacts, isLoading: contactsLoading} = useSupplierContactsQuery(entityId);
    const {data: suggestions} = useSupplierSuggestionsQuery(entityId);
    const aliasSuggestions = (suggestions ?? []).filter(s => s.field === 'alias' && s.state === 'open');
    const domainSuggestion = (suggestions ?? []).find(s => s.field === 'domain' && s.state === 'open');
    // Accepting an alias suggestion only records the decision server-side (useDecideSuggestionMutation's
    // own comment) — there's no ClusterMember write to refetch, so an accepted suggestion has to be
    // folded in here directly or it vanishes from the UI the moment it leaves aliasSuggestions above.
    const acceptedAliases = (suggestions ?? []).filter(s => s.field === 'alias' && s.state === 'accepted')
        .map(s => s.value);

    const aliases = [...new Set(
        [...clusterMembers.map(m => m.normalized_name), ...acceptedAliases].filter(n => n !== entityName),
    )];

    return (
        <CustomCard
            title="Identity"
        >
            <Stack gap={2}>
                <SupplierInfoField entityId={entityId} summary={summary}/>

                <GoodsAndServicesField entries={goodsAndServices}/>

                <Stack direction="row" gap={4}>
                    <Stack gap={2} sx={{pt: 1, flex: 1}}>
                        {(aliases.length > 0 || aliasSuggestions.length > 0) && (
                            <Stack gap={1} sx={{flex: 1}}>
                                <Typography variant="caption" color="grey.700">Aliases</Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {aliases.slice(0, 5).map(a => <Tag key={a} label={a} size="small" variant="outlined"
                                                                       color="default"/>)}
                                </Stack>
                                <AliasSuggestions suggestions={aliasSuggestions}/>
                            </Stack>
                        )}
                        <Stack gap={1} sx={{flex: 1}}>
                            <Typography variant="caption" color="grey.700">Domain</Typography>
                            <DomainField
                                entityId={entityId}
                                profileId={enrichment?.id}
                                domain={enrichment?.contact_info?.domain ?? ''}
                                isLoading={enrichmentLoading}
                            />
                            {!enrichment?.contact_info?.domain && domainSuggestion
                                && <DomainSuggestion suggestion={domainSuggestion}/>}
                        </Stack>
                        <Stack gap={1} sx={{flex: 1}}>
                            <Typography variant="caption" color="grey.700">Countries</Typography>
                            <SupplierCountryFlag country={entityCountry}/>
                        </Stack>
                        <Stack gap={1} sx={{flex: 1}}>
                            <Typography variant="caption" color="grey.700">Registered address</Typography>
                            <Typography variant="body2"
                                        color="primary">{enrichment?.contact_info?.address || entityCity || '—'}</Typography>
                        </Stack>
                        <ParentField/>
                    </Stack>

                    <Stack gap={2} sx={{pt: 1, flex: 1}}>
                        {SUPPLIER_CONTACT_TYPES.map(contactType => (
                            <SupplierContactsField
                                key={contactType}
                                entityId={entityId}
                                contactType={contactType}
                                contacts={(contacts ?? []).filter(c => c.contact_type === contactType)}
                                isLoading={contactsLoading}
                            />
                        ))}
                    </Stack>
                    <Box sx={{flex: 1}}>
                        <SegmentationBlock entityId={entityId} supplierName={entityName}/>
                    </Box>
                </Stack>
            </Stack>
        </CustomCard>
    );
};
